import React, { FC, useEffect, useMemo } from "react";
import Glide from "@glidejs/glide";
import NcImage from "shared/NcImage/NcImage";
import NextPrev from "shared/NextPrev/NextPrev";
import useNcId from "hooks/useNcId";

export interface GallerySliderProps {
  className?: string;
  galleryImgs: string[];
  ratioClass?: string;
  uniqueID: string;
  href?: string;  // you can omit this now if you never want images clickable
}

const GallerySlider: FC<GallerySliderProps> = ({
  className = "",
  galleryImgs,
  ratioClass = "aspect-w-4 aspect-h-3",
  uniqueID,
  // href prop no longer needed if you’re handling navigation at the card level
}) => {
  const UNIQUE_CLASS = `gallerySlider__${uniqueID}` + useNcId();

  useEffect(() => {
    let glideInstance: Glide | null = null;
    if (galleryImgs.length > 1) {
      glideInstance = new Glide(`.${UNIQUE_CLASS}`, {
        perView: 1,
        gap: 0,
        keyboard: false,
      });
      glideInstance.mount();
    }
    return () => {
      if (glideInstance) glideInstance.destroy();
    };
  }, [UNIQUE_CLASS, galleryImgs]);

  const renderDots = () => {
    if (galleryImgs.length < 2) return null;
    return (
      <div
        className="glide__bullets flex items-center justify-center absolute bottom-2 left-1/2 transform -translate-x-1/2 space-x-1.5"
        data-glide-el="controls[nav]"
        // stop propagation so clicking dots doesn’t bubble
        onClick={e => e.stopPropagation()}
      >
        {galleryImgs.slice(0, 4).map((_, i) => (
          <button
            key={i}
            className="glide__bullet w-1.5 h-1.5 rounded-full bg-neutral-300"
            data-glide-dir={`=${i}`}
          />
        ))}
      </div>
    );
  };

  const renderSliderGallery = () => (
    <div className={`${UNIQUE_CLASS} relative group overflow-hidden`}>
      <div className="glide__track" data-glide-el="track">
        <ul className="glide__slides">
          {galleryImgs.map((item, index) => (
            <li key={index} className="glide__slide">
              <div className={`block ${ratioClass}`}>
                <NcImage src={item} />
              </div>
            </li>
          ))}
        </ul>
      </div>

      {galleryImgs.length > 1 && (
        <>
          <div className="absolute -bottom-4 inset-x-0 h-10 bg-gradient-to-t from-neutral-900" />
          
          {renderDots()}

          <div
            // stopPropagation prevents parent card click
            onClick={e => e.stopPropagation()}
            className="absolute opacity-0 group-hover:opacity-100 transition-opacity flex top-1/2 transform -translate-y-1/2 inset-x-2 justify-between"
          >
            <NextPrev className="w-full justify-between" btnClassName="w-8 h-8" />
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={`nc-GallerySlider ${className}`} data-nc-id="GallerySlider">
      {renderSliderGallery()}
    </div>
  );
};

export default GallerySlider;